import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  roleHasPermissions,
  type Integration,
  type IntegrationProvider,
} from '@lizrd/contracts';
import { getDomain, listIntegrations } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Alert } from '../components/ui/alert';
import { SkeletonChart } from '../components/ui/skeleton';
import { IntegrationsGuide } from '../components/IntegrationsGuide';
import { IntegrationFlow } from '../components/IntegrationFlow';
import { IntegrationDetailModal } from '../components/IntegrationDetailModal';
import { ConnectSourceFlow } from '../components/ConnectSourceFlow';

/** Appended to every "connected" toast: a sync can take a full cycle to complete. */
const CONNECT_SYNC_NOTE =
  ' — it can take up to an hour for Lizrd to fully sync its resources, cost, and recommendations.';

/** Friendly label for the "connected" toast, per provider. */
const PROVIDER_LABEL: Partial<Record<IntegrationProvider, string>> = {
  AWS: 'AWS',
  GCP: 'GCP',
  AZURE: 'Azure',
};

export function IntegrationsPage() {
  const { domainId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role);
  // The sandbox is a read-only demo — it can view the connected-sources graph but
  // not connect/disconnect/manage (also enforced 403 server-side).
  const isSandbox = useAuthStore((s) => s.user?.organizationKind === 'SANDBOX');
  const canManage =
    !isSandbox && (role ? roleHasPermissions(role, [Permission.IntegrationsManage]) : false);
  const qc = useQueryClient();

  const [selected, setSelected] = useState<Integration | null>(null);
  const [picking, setPicking] = useState(false);
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const domain = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => getDomain(domainId),
    enabled: !!domainId,
  });
  const integrations = useQuery({
    queryKey: ['integrations', domainId],
    queryFn: () => listIntegrations(domainId),
    enabled: !!domainId,
    refetchInterval: 5000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations', domainId] });

  // Handle the return from the GitHub install flow (?github=connected|error).
  useEffect(() => {
    const status = searchParams.get('github');
    if (!status) return;
    setBanner(
      status === 'connected'
        ? { ok: true, text: `GitHub connected${CONNECT_SYNC_NOTE}` }
        : { ok: false, text: 'GitHub setup did not complete. Please try again.' },
    );
    invalidate();
    searchParams.delete('github');
    setSearchParams(searchParams, { replace: true });
  }, []);

  return (
    <div className="space-y-4">
      {banner && <Alert variant={banner.ok ? 'success' : 'danger'}>{banner.text}</Alert>}

      <IntegrationsGuide />

      <div>
        <h2 className="font-display text-lg font-bold text-bone">Connected Sources</h2>
        <p className="text-xs text-muted">
          Watch your code and cloud data flow into this domain — click a source for details, or
          connect a new one.
        </p>
      </div>

      {integrations.isLoading ? (
        <SkeletonChart height="h-[460px]" />
      ) : (
        <IntegrationFlow
          integrations={integrations.data ?? []}
          envName={domain.data?.name ?? 'Domain'}
          canManage={canManage}
          onSelect={setSelected}
          onConnect={() => setPicking(true)}
        />
      )}

      {!canManage && (
        <p className="text-xs text-muted">
          You have read-only access — connecting or removing sources needs manager permissions.
        </p>
      )}

      {selected && (
        <IntegrationDetailModal
          envId={domainId}
          integration={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
        />
      )}
      <ConnectSourceFlow
        envId={domainId}
        active={picking}
        onClosePicker={() => setPicking(false)}
        onConnected={(provider) => {
          const what = PROVIDER_LABEL[provider] ?? 'Integration';
          setBanner({ ok: true, text: `${what} connected${CONNECT_SYNC_NOTE}` });
          invalidate();
        }}
      />
    </div>
  );
}
